import * as React from 'react';
import { useEffect, useState } from 'react';
import { Form, Formik, useFormikContext } from 'formik';
import { useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import { selectActiveDepositLimit } from '@/store/selectors/limitsSelectors';
import { RootState } from '@/models/root-state.model';
import { selectAuthToken } from '@/store/selectors/authSelectors';
import { selectUserCurrency, selectUserEmail } from '@/store/selectors/userSelectors';
import * as Yup from 'yup';
import { getLimits } from '@/store/actions/limitsActions';
import { depositLimits, generateBffErrorMessageWithCode } from '@/utils/utils';
import { formatCurrency } from '@/utils/currency-utils';
import { selectLocale } from '@/store/selectors/commonSelectors';
import { apiV2ErrorHandler } from '@/shared/errorHandler';
import { AxiosError } from 'axios';
import { setUserEmail } from '@/store/actions/user';
import { useAbsoluteNavigate } from '@/hooks/useAbsoluteNavigate';
import { BffBonusData, BffUserActiveLimit } from '@lucky7ventures/bff-types';
import { selectUserBalance } from '@/store/wallet/selectors';
import { getUserWallet } from '@/store/wallet/actions';
import {
  BffResponsibleGamingSetUserLimitsRequest,
  LimitDuration,
  LimitType,
} from '@lucky7ventures/bff-types';
import { useBffApiRequest } from '@/hooks/useBffApiRequest';
import BffApiService from '@/shared/bffApiService';
import { BffApiError } from '@/models/bff-api-error';
import { selectActiveBonuses } from '@/store/bonuses/selectors';
import { getBonuses } from '@/store/bonuses/actions';
import { FormInput } from '@/components/FormInput';
import { BtnOneLineBlue } from '@/components/buttons/BtnOneLineBlue';
import { BtnBasicLink } from '@/components/buttons/BtnBasicLink';
import { Alert } from '@/components/Alert';
import Spinner from '@/components/UI/Spinner';
import Currency from '@/components/UI/Currency';
import { BtnOneLineRed } from '@/components/buttons/BtnOneLineRed';

enum FlowStep {
  SetDepositLimits,
  Refund,
}

interface DepositFormValues {
  depositLimitDay: string | number;
  depositLimitWeek: string | number;
  depositLimitMonth: string | number;
  email: string;
}

const DEPOSIT_LIMITS = depositLimits();

// there is a special workflow in the BO, where a user needs to be sent an email if they set a deposit limit above 10k
const DEPOSIT_LIMIT_EMAIL_TRIGGER = 10000;

function ConnectedDeposits({
  limitWeek,
  limitMonth,
}: {
  limitWeek: string | number;
  limitMonth: string | number;
}): null {
  const {
    values: { depositLimitDay },
    setFieldValue,
  } = useFormikContext<{ depositLimitDay: number }>();

  useEffect(() => {
    if (!depositLimitDay) {
      if (!limitWeek) {
        setFieldValue('depositLimitWeek', '');
      }
      if (!limitMonth) {
        setFieldValue('depositLimitMonth', '');
      }
      return;
    }

    const depositLimitWeek = depositLimitDay * 7;
    const depositLimitMonth = depositLimitDay * 30;

    if (!limitWeek) {
      setFieldValue(
        'depositLimitWeek',
        depositLimitWeek > DEPOSIT_LIMITS.week ? DEPOSIT_LIMITS.week : depositLimitWeek,
      );
    }
    if (!limitMonth) {
      setFieldValue(
        'depositLimitMonth',
        depositLimitMonth > DEPOSIT_LIMITS.month ? DEPOSIT_LIMITS.month : depositLimitMonth,
      );
    }
  }, [depositLimitDay]);

  return null;
}

function SetDepositsStep({
  initialValues,
  submitCallback,
  logoutCallback,
  depositsUpdating,
  depositsError,
  limitDay,
  limitWeek,
  limitMonth,
  emailCompleted,
}: {
  initialValues: DepositFormValues;
  submitCallback: (values: DepositFormValues) => void;
  logoutCallback: () => void;
  depositsUpdating: boolean;
  depositsError: string | null;
  limitDay: BffUserActiveLimit | undefined;
  limitWeek: BffUserActiveLimit | undefined;
  limitMonth: BffUserActiveLimit | undefined;
  emailCompleted: boolean;
}): JSX.Element {
  const intl = useIntl();

  const limitDayError = intl.formatMessage(
    { id: 'depositLimit.amount.error' },
    { min: DEPOSIT_LIMITS.min, max: DEPOSIT_LIMITS.day.toLocaleString() },
  );
  const limitWeekError = intl.formatMessage(
    { id: 'depositLimit.amount.error' },
    { min: DEPOSIT_LIMITS.min, max: DEPOSIT_LIMITS.week.toLocaleString() },
  );
  const limitMonthError = intl.formatMessage(
    { id: 'depositLimit.amount.error' },
    { min: DEPOSIT_LIMITS.min, max: DEPOSIT_LIMITS.month.toLocaleString() },
  );

  const validationSchema = Yup.object().shape({
    depositLimitDay: Yup.number()
      .typeError(limitDayError)
      .min(DEPOSIT_LIMITS.min, limitDayError)
      .max(DEPOSIT_LIMITS.day, limitDayError)
      .required(limitDayError),
    depositLimitWeek: Yup.number()
      .typeError(limitWeekError)
      .min(DEPOSIT_LIMITS.min, limitWeekError)
      .max(DEPOSIT_LIMITS.week, limitWeekError)
      .required(limitWeekError),
    depositLimitMonth: Yup.number()
      .typeError(limitMonthError)
      .min(DEPOSIT_LIMITS.min, limitMonthError)
      .max(DEPOSIT_LIMITS.month, limitMonthError)
      .required(limitMonthError),
    email: Yup.string().when(['depositLimitDay', 'depositLimitWeek', 'depositLimitMonth'], {
      is: (limitDay: number, limitWeek: number, limitMonth: number) => {
        return (
          limitDay >= DEPOSIT_LIMIT_EMAIL_TRIGGER ||
          limitWeek >= DEPOSIT_LIMIT_EMAIL_TRIGGER ||
          limitMonth >= DEPOSIT_LIMIT_EMAIL_TRIGGER
        );
      },
      then: schema =>
        schema
          .required(intl.formatMessage({ id: 'inputs.email.required' }))
          .email(intl.formatMessage({ id: 'inputs.email.invalid' })),
      otherwise: schema => schema.notRequired(),
    }),
  });

  const depositFormat = (value: string): string => {
    if (!value) {
      return '';
    }

    const parsed = parseInt(value);

    if (isNaN(parsed)) {
      return '';
    }

    return parsed.toString();
  };

  return (
    <div>
      <h1 className="mb-8 text-2xl">
        {intl.formatMessage({ id: 'modal.requiredDepositLimits.header' })}
      </h1>
      <div>
        <Formik
          validationSchema={validationSchema}
          initialValues={initialValues}
          onSubmit={(values: DepositFormValues) => submitCallback(values)}
        >
          {({ isValid, values }) => {
            return (
              <Form>
                <FormInput
                  name="depositLimitDay"
                  label={intl.formatMessage({
                    id: 'updateContactInfo.modal.depositLimitDaily',
                  })}
                  placeholder={intl.formatMessage(
                    { id: 'updateContactInfo.modal.depositLimitPlaceholder' },
                    { min: DEPOSIT_LIMITS.min, max: DEPOSIT_LIMITS.day.toLocaleString() },
                  )}
                  formatOnChange={depositFormat}
                  completed={!!limitDay}
                />
                <FormInput
                  name="depositLimitWeek"
                  label={intl.formatMessage({
                    id: 'updateContactInfo.modal.depositLimitWeekly',
                  })}
                  placeholder={intl.formatMessage(
                    { id: 'updateContactInfo.modal.depositLimitPlaceholder' },
                    { min: DEPOSIT_LIMITS.min, max: DEPOSIT_LIMITS.week.toLocaleString() },
                  )}
                  completed={!!limitWeek}
                  formatOnChange={depositFormat}
                />
                <FormInput
                  name="depositLimitMonth"
                  label={intl.formatMessage({
                    id: 'updateContactInfo.modal.depositLimitMonthly',
                  })}
                  placeholder={intl.formatMessage(
                    { id: 'updateContactInfo.modal.depositLimitPlaceholder' },
                    { min: DEPOSIT_LIMITS.min, max: DEPOSIT_LIMITS.month.toLocaleString() },
                  )}
                  completed={!!limitMonth}
                  formatOnChange={depositFormat}
                />
                {(values.depositLimitDay >= DEPOSIT_LIMIT_EMAIL_TRIGGER ||
                  values.depositLimitWeek >= DEPOSIT_LIMIT_EMAIL_TRIGGER ||
                  values.depositLimitMonth >= DEPOSIT_LIMIT_EMAIL_TRIGGER) && (
                  <FormInput
                    name="email"
                    type="email"
                    label={intl.formatMessage({
                      id: 'inputs.email',
                    })}
                    placeholder={intl.formatMessage({
                      id: 'inputs.email.placeholder',
                    })}
                    hint={intl.formatMessage({ id: 'requiredDepositLimits.email.hint' })}
                    completed={emailCompleted}
                  />
                )}
                <div className="mt-8 mb-4">
                  <BtnOneLineBlue
                    onClickHandler={() => submitCallback(values)}
                    isLoading={depositsUpdating}
                    disabled={!isValid}
                    text={intl.formatMessage({ id: 'misc.update' })}
                  />
                </div>
                <ConnectedDeposits
                  limitWeek={initialValues.depositLimitWeek}
                  limitMonth={initialValues.depositLimitMonth}
                />
              </Form>
            );
          }}
        </Formik>
        {depositsError && <Alert text={depositsError} />}
        <BtnBasicLink
          onClickHandler={logoutCallback}
          text={intl.formatMessage({ id: 'header.logout' })}
        />
      </div>
    </div>
  );
}

function RefundStep({
  realBalance,
  limits,
  bonuses,
  reviseLimitsCallback,
  currency,
  refundCallback,
  refundInProgress,
  refundError,
  locale,
}: {
  realBalance: number;
  limits: DepositFormValues;
  bonuses: BffBonusData[];
  reviseLimitsCallback: () => void;
  currency: string;
  refundCallback: (values: DepositFormValues) => void;
  refundInProgress: boolean;
  refundError: boolean;
  locale: string;
}): JSX.Element {
  const intl = useIntl();

  const minLimit = Math.min(
    +limits.depositLimitDay,
    +limits.depositLimitWeek,
    +limits.depositLimitMonth,
  );
  const refundAmount = realBalance - minLimit;

  if (refundInProgress) {
    return (
      <div>
        <h1 className="mb-8 text-xl">{intl.formatMessage({ id: 'limits.refund.loading' })}</h1>
        <Spinner />
      </div>
    );
  }

  if (refundError) {
    return (
      <div>
        <p className="mb-8 text-xl">{intl.formatMessage({ id: 'error.support' })}</p>
        <BtnOneLineBlue
          onClickHandler={reviseLimitsCallback}
          isLoading={false}
          text={intl.formatMessage({ id: 'misc.tryAgain' })}
        />
      </div>
    );
  }

  return (
    <div>
      <h1 className="mb-8 text-2xl">{intl.formatMessage({ id: 'limits.refund.title' })}</h1>
      <div>
        <p>
          {intl.formatMessage(
            { id: 'limits.refund.text' },
            {
              amount: (
                <span className="font-bold">
                  <Currency currency={currency} amount={realBalance} />
                </span>
              ),
            },
          )}
        </p>
        <BtnOneLineBlue
          onClickHandler={reviseLimitsCallback}
          isLoading={false}
          text={intl.formatMessage({ id: 'limits.refund.btn.changeLimits' })}
        />
        <div className="my-8 h-[1px] bg-gray-300"></div>
        <p>
          {intl.formatMessage(
            { id: 'limits.refund.disclaimer' },
            {
              amount: (
                <span className="font-bold">
                  <Currency currency={currency} amount={refundAmount} />
                </span>
              ),
            },
          )}
        </p>
      </div>
      {bonuses.length > 0 && (
        <div className="underline">
          <p>{intl.formatMessage({ id: 'limits.refund.bonuses.disclaimer' })}</p>
        </div>
      )}
      <BtnOneLineRed
        onClickHandler={() => refundCallback(limits)}
        isLoading={false}
        text={intl.formatMessage(
          { id: 'limits.refund.btn' },
          { amount: formatCurrency(refundAmount, locale, currency, 0) },
        )}
      />
    </div>
  );
}

export function RequiredDepositLimitsModal({
  closeModal,
}: {
  closeModal: () => void;
}): JSX.Element {
  const intl = useIntl();
  const token = useSelector(selectAuthToken);
  const { request: setLimitsRequest, loading: setLimitsLoading } = useBffApiRequest();
  const {
    request: refundLimitsRequest,
    error: refundError,
    loading: refundLoading,
  } = useBffApiRequest();
  const dispatch = useDispatch();
  const [flowStep, setFlowStep] = useState<FlowStep>(FlowStep.SetDepositLimits);
  const absoluteNavigate = useAbsoluteNavigate();
  const realBalance = useSelector(selectUserBalance);
  const locale = useSelector(selectLocale);
  const userCurrency = useSelector(selectUserCurrency);
  const userEmail = useSelector(selectUserEmail);
  const activeBonuses = useSelector(selectActiveBonuses);
  const limitDay = useSelector((state: RootState) =>
    selectActiveDepositLimit(state, LimitDuration._24Hours),
  );
  const limitWeek = useSelector((state: RootState) =>
    selectActiveDepositLimit(state, LimitDuration._1Week),
  );
  const limitMonth = useSelector((state: RootState) =>
    selectActiveDepositLimit(state, LimitDuration._1Month),
  );
  const [depositLimits, setDepositLimits] = useState({
    depositLimitDay: limitDay ? limitDay.amount : '',
    depositLimitWeek: limitWeek ? limitWeek.amount : '',
    depositLimitMonth: limitMonth ? limitMonth.amount : '',
    email: userEmail,
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const submitDeposits = async (values: DepositFormValues) => {
    setDepositLimits(values);
    setError('');
    try {
      if (values.email !== userEmail) {
        setLoading(true);
        await BffApiService.changeUserEmail({ payload: { email: values.email } });
        dispatch(setUserEmail(values.email));
        setLoading(false);
      }

      // here we want to determine if we can set the limits immediately or if we need to prompt the user for the refund flow
      if (realBalance === null) {
        return;
      }

      // Check if the user has set one of the limits that would go under his already deposited Balance
      const minLimit = Math.min(
        +values.depositLimitDay,
        +values.depositLimitWeek,
        +values.depositLimitMonth,
      );

      if (realBalance > minLimit) {
        // if the real balance is over the min limit we want to trigger the refund flow
        setFlowStep(FlowStep.Refund);
        return;
      }

      const limits: BffResponsibleGamingSetUserLimitsRequest = [
        {
          type: LimitType.Deposit,
          duration: LimitDuration._24Hours,
          amount: Number(values.depositLimitDay),
        },
        {
          type: LimitType.Deposit,
          duration: LimitDuration._1Week,
          amount: Number(values.depositLimitWeek),
        },
        {
          type: LimitType.Deposit,
          duration: LimitDuration._1Month,
          amount: Number(values.depositLimitMonth),
        },
      ];

      setLimitsRequest({
        apiMethod: BffApiService.setUserLimits,
        payload: limits,
        successCallback: () => {
          dispatch(getLimits());
          closeModal();
        },
        errorCallback: (error: BffApiError) => {
          const message = generateBffErrorMessageWithCode(
            intl.formatMessage({ id: 'error.support' }),
            error.code,
          );
          setError(message);
        },
      });
    } catch (e) {
      setLoading(false);
      if (!(e instanceof AxiosError)) {
        setError(intl.formatMessage({ id: 'error.support' }));
      } else {
        const error = apiV2ErrorHandler(e);
        if (error.error === 'EMAIL_ALREADY_EXISTS') {
          setError(intl.formatMessage({ id: 'error.emailAlreadyExists' }));
        } else {
          setError(`${intl.formatMessage({ id: 'error.support' })} (${error.errorId})`);
        }
      }
    }
  };

  const initiateRefund = (values: DepositFormValues): void => {
    if (!token) {
      return;
    }

    const limits = [
      {
        type: LimitType.Deposit,
        duration: LimitDuration._24Hours,
        amount: Number(values.depositLimitDay),
      },
      {
        type: LimitType.Deposit,
        duration: LimitDuration._1Week,
        amount: Number(values.depositLimitWeek),
      },
      {
        type: LimitType.Deposit,
        duration: LimitDuration._1Month,
        amount: Number(values.depositLimitMonth),
      },
    ];

    const onSuccess = () => {
      dispatch(getLimits());
      dispatch(getUserWallet());
      dispatch(getBonuses());
      closeModal();
    };

    refundLimitsRequest({
      apiMethod: BffApiService.limitsRefund,
      payload: limits,
      successCallback: onSuccess,
    });
  };

  const logout = (): void => {
    closeModal();
    absoluteNavigate('/logout');
  };

  if (realBalance === null) {
    return (
      <div className="relative w-full max-w-sm rounded-lg bg-slate-50 px-8 pt-12 pb-8">
        <h1 className="mt-4 text-lg font-bold">Opps, något gick fel</h1>
        <p className="mt-3 mb-0">Vänligen ladda om sidan för att nå ditt spelkonto.</p>
      </div>
    );
  }

  return (
    <div className="relative w-full max-w-sm rounded-lg bg-slate-50 px-6 pt-12 pb-8">
      {flowStep === FlowStep.SetDepositLimits && (
        <SetDepositsStep
          initialValues={depositLimits}
          submitCallback={submitDeposits}
          logoutCallback={logout}
          depositsUpdating={setLimitsLoading || loading}
          depositsError={error}
          limitDay={limitDay}
          limitWeek={limitWeek}
          limitMonth={limitMonth}
          emailCompleted={!!userEmail}
        />
      )}
      {flowStep === FlowStep.Refund && (
        <RefundStep
          realBalance={realBalance}
          limits={depositLimits}
          bonuses={activeBonuses}
          currency={userCurrency}
          reviseLimitsCallback={() => {
            initiateRefund(depositLimits);
            setFlowStep(FlowStep.SetDepositLimits);
          }}
          refundInProgress={refundLoading}
          refundError={!!refundError}
          refundCallback={initiateRefund}
          locale={locale}
        />
      )}
    </div>
  );
}
